import { ChangeDetectionStrategy, Component, ElementRef, inject } from '@angular/core';
import { HeaderComponent, PageHeaderBackSwipeConfig } from '@app/components/header/header.component';
import { ActivitySectionDirective, UserActivityClickTrackingDirective } from '@core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { TextComponent } from '@app/components/text/text.component';
import { BrightnessService, TintingMode } from '@app/services/brightness';
import { LanguageService } from '@app/services';
import { HeaderTextComponent } from '@app/components/header/header-text/header-text.component';

@Component({
  selector: 'app-page-preferences',
  templateUrl: './preferences.component.html',
  styleUrl: './preferences.component.scss',
  imports: [
    ActivitySectionDirective,
    HeaderComponent,
    UserActivityClickTrackingDirective,
    TranslatePipe,
    TextComponent,
    HeaderTextComponent,
  ],
  host: {
    'class': 'page',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreferencesComponent {

  private readonly translate = inject(TranslateService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly brightnessService = inject(BrightnessService);
  private readonly languageService = inject(LanguageService);

  protected readonly backSwipeConfig: PageHeaderBackSwipeConfig = {
    element: this.elementRef.nativeElement,
  };

  protected readonly currentLanguage = toSignal(this.translate.onLangChange.pipe(
    map((event) => {
      return event.lang;
    }),
  ), { initialValue: this.translate.getCurrentLang() });

  protected readonly languagesConfig = toSignal(this.languageService.config$, {
    initialValue: {
      allowUserChange: false,
      supported: [],
    },
  });

  protected readonly brightnessConfig = toSignal(this.brightnessService.config$, {
    initialValue: {
      allowUserChange: false,
    },
  });

  protected readonly tintingModes = Object.keys(this.brightnessService.tintingModes) as TintingMode[];

  protected readonly currentTintingMode = toSignal(this.brightnessService.tinting$.pipe(
    map(() => this.brightnessService.getTintingMode()),
  ), { initialValue: 'high' });

  public onLanguageSelect(lang: string): void {
    this.translate.use(lang);
  }

  public onTintingChangeMode(mode: TintingMode): void {
    this.brightnessService.setTintingMode(mode);
  }

}
